import { Heart } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useLang } from "@/context/LanguageContext";

/**
 * Heart icon toggle for adding/removing a product from the logged-in customer wishlist.
 * If the visitor is not logged in, we route them to /login with a redirect back here.
 *
 * Variants:
 *  - "card"  : absolute-positioned overlay top-right (used inside product image tiles)
 *  - "inline": inline button (used on ProductDetail beside "Add to cart")
 */
export default function WishlistButton({ productId, variant = "card", className = "" }) {
  const { customer, isInWishlist, addToWishlist, removeFromWishlist } = useCustomerAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const active = customer ? isInWishlist(productId) : false;

  const toggle = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!customer) {
      toast.info(t("wl_login_hint"), { duration: 3000 });
      nav(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setBusy(true);
    try {
      if (active) {
        await removeFromWishlist(productId);
        toast.success(t("wl_removed"));
      } else {
        await addToWishlist(productId);
        toast.success(t("wl_added"));
        // Meta Pixel + CAPI: AddToWishlist
        if (typeof window !== "undefined" && typeof window.audrivioTrack === "function") {
          window.audrivioTrack("AddToWishlist", {
            content_ids: [productId],
            content_type: "product",
            currency: "EUR",
          });
        }
      }
    } catch {
      toast.error(t("wl_error"));
    } finally {
      setBusy(false);
    }
  };

  if (variant === "inline") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid={`wishlist-toggle-${productId}`}
        aria-pressed={active}
        aria-label={active ? t("wl_aria_remove") : t("wl_aria_add")}
        className={`inline-flex items-center gap-2 h-11 px-4 rounded-lg border transition-colors disabled:opacity-60 ${active ? "border-xd-orange bg-xd-orange/10 text-xd-orange" : "border-xd-border text-xd-text hover:border-xd-orange/60 hover:text-xd-orange"} ${className}`}
      >
        <Heart className={`w-4 h-4 ${active ? "fill-xd-orange" : ""}`} />
        <span className="text-sm font-medium">{active ? t("wl_in_list") : t("wl_label")}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`wishlist-toggle-${productId}`}
      aria-pressed={active}
      aria-label={active ? t("wl_aria_remove") : t("wl_aria_add")}
      className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-white/95 backdrop-blur border border-white shadow-sm hover:scale-110 active:scale-95 transition disabled:opacity-60 ${active ? "text-xd-orange" : "text-slate-600 hover:text-xd-orange"} ${className}`}
    >
      <Heart className={`w-4 h-4 ${active ? "fill-xd-orange" : ""}`} />
    </button>
  );
}
